<template>
  <div id="add-user-customer-access">
    <right-side-bar
      fixed
      divider
      prepend
      width="30%"
      header-style="mt-2 mr-2"
      @toggle-right-bar="toogleDrawer"
      :is-right-drawer-open="isDrawerOpen"
      :title="$dictionary.app.manageUsers.user.customers.subTitle"
    >
      <v-form
        ref="addCustomerForm"
        v-model="isFormValid"
        @submit.prevent="onSubmitHandler"
      >
        <v-autocomplete
          outlined
          item-text="name"
          label="Customer"
          :items="customersList"
          :search-input.sync="search"
          class="required pt-5"
          item-value="id"
          return-object
          clearable
          open-on-clear
          :loading="areCustomersLoading"
          v-model="formData.customer"
          :rules="[required('Customer')]"
          :menu-props="{ bottom: true, offsetY: true }"
          :no-data-text="$dictionary.app.common.customers.noData"
        >
          <template #prepend-item>
            <span v-intersect="loadMoreCustomers" />
          </template>
        </v-autocomplete>
        <alert-message
          :type="$appConfig.alert.types.info"
          :message="$dictionary.app.manageUsers.user.customers.alertMessage"
        />
        <div class="form-group">
          <v-divider class="mt-4 mb-6 border-light-grey" />
          <v-btn
            type="submit"
            color="primary"
            :loading="isLoading"
            :disabled="!isFormValid"
            id="add-user-customer-submit-btn"
            class="px-4 button font-weight-bold"
          >
            Save
          </v-btn>
        </div>
      </v-form>
    </right-side-bar>
  </div>
</template>

<script>
import RightSideBar from "./RightSideBar.vue";
import { mapGetters, mapActions } from "vuex";
import { required } from "@/validators";

import { AlertMessage } from "@/components/shared";
import { isEmpty, isResponseCode } from "@/utils/common.utils";
import { addCustomerOwner } from "@/services";

import debounce from "lodash/debounce";
import {
  DEBOUNCE_MILLIS,
  RESPONSE_CODES,
  SORTING_ORDERS,
  TABLE_PER_PAGES_OPTIONS,
} from "@/constants";

/**
 * Add user customer drawer form
 */
export default {
  name: "AddUserCustomerDrawer",
  /**
   * ---------------- Custom events ------------------
   */
  emits: ["toogle-drawer", "update-records"],
  /**
   * ---------------- Props ------------------
   */
  props: {
    isDrawerOpen: { type: Boolean, required: true, default: false },
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    RightSideBar,
    AlertMessage,
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    ...mapGetters({
      customers: "customer/customers",
    }),
    /**
     * Add customer access to user request payload
     * @type {Object}
     */
    payload() {
      const { customer } = this.formData;
      return { owners: { owner_id: customer.id } };
    },
    /**
     * Fetch customer params
     */
    params() {
      return {
        "q[name_cont]": this.search,
        per_page: TABLE_PER_PAGES_OPTIONS[1],
        page: this.currentPage,
        "q[s]": `name ${SORTING_ORDERS.asc}`,
      };
    },
  },
  /**
   * ---------------- Data Properties ------------------
   */
  data() {
    const { snackbarTypes } = this.$appConfig.snackbar;
    return {
      formData: {
        customer: [],
      },
      isFormValid: false,
      areCustomersLoading: false,
      isLoading: false,
      snackbarTypes,
      search: "",
      currentPage: 1,
      customersList: [],
      hasRecords: true,
    };
  },
  /**
   * ---------------- Watching Properties ------------------
   */
  watch: {
    search() {
      this.lazyLoadCustomers();
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    required,
    /**
     * Lazy load customers
     * @type Debounce function
     */
    lazyLoadCustomers: debounce(function() {
      this.currentPage = 1;
      this.getCustomersList();
    }, DEBOUNCE_MILLIS),
    /**
     * Load customer records on scroll
     */
    loadMoreCustomers() {
      this.currentPage++;
      this.getCustomersList();
    },

    /**
     * Maps store actions in the component
     */
    ...mapActions({
      getCustomers: "customer/getCustomers",
      setSnackbar: "ui/setSnackbar",
    }),

    /**
     * Toogles drawer
     * @param {Boolean} val Updated value of the isDrawerOpen prop
     * @emits toogle-drawer
     */
    toogleDrawer(val) {
      this.$emit("toogle-drawer", val);
    },
    /**
     * Form submit handler
     */
    async onSubmitHandler() {
      try {
        if (!this.isFormValid) return this.$refs.addCustomerForm.validate();
        this.isLoading = true;

        const { id } = this.$route.params;
        const { status } = await addCustomerOwner(id, this.payload);

        if (isResponseCode(status, RESPONSE_CODES.success)) {
          this.setSnackbar({
            value: true,
            message: this.$dictionary.app.manageUsers.user.customers.successMsg,
            type: this.snackbarTypes.success,
          });
        }

        this.$refs.addCustomerForm.reset();

        this.toogleDrawer(false);
        this.$emit("update-records");
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Fetched list of customers
     * @param {Object} params Customer request query params
     */
    async getCustomersList(params = this.params) {
      try {
        this.areCustomersLoading = true;

        await this.getCustomers(params);
        this.customersList = [...this.customersList, ...this.customers];
        this.hasRecords = !isEmpty(this.customers);
      } finally {
        this.areCustomersLoading = false;
      }
    },
  },
  /**
  |--------------------------------------------------
  | Created lifecycle hook
  |--------------------------------------------------
  */
  created() {
    this.getCustomersList();
  },
};
</script>
